/* eslint-disable react-hooks/exhaustive-deps */
import { TrackingEventTypes } from '@eagle/data-function-types';
import { Box, Dialog, IconButton, styled, SwipeableDrawer, Typography } from '@ethings/ui-toolkit';
import { useTheme } from '@mui/material';
import { GridEventListener } from '@mui/x-data-grid';
import chroma from 'chroma-js';
import L from 'leaflet';
import React, { FC, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Marker, useMap } from 'react-leaflet';
import { useSmallScreen } from '../../../../hooks';
import { MapPanes } from '../../../../types';
import { useHistorySearch, useNotableEventsContext } from '../../../entity-journey';
import { Icon } from '../../../icons';
import { EventLocationData } from '../../thing-event-pane';
import { ClusterProps } from '../route-matching/clustering.types';
import { getNotableEventColor } from '../route-matching/clustering.utils';
import { ClusterTabularData } from './cluster-tabular-data';
import { ClusterTooltip } from './cluster-tooltip';
import './cluster.styles.css';
import { BOUNDING_RADIUS, createBoundingMarker, createBubbleClusterMarker, createClusterMarker, getClusterBubbleMarkerSize, getClusterMarkerSize, getClusterTooltipOffset, getMarkerSize, getNotableEventsInformation } from './cluster.utils';
import { EventClusterPoint } from './event-cluster-point';

const StyledSwipeableDrawer = styled(SwipeableDrawer)(({ theme }) => ({
  '&.MuiDrawer-root > .MuiPaper-root': {
    background: theme.palette.background.paper,
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
}));

const getEventDialogIndex = (eventId: string, items: EventLocationData[]): number => {
  return items.findIndex((event) => event.eventId === eventId);
};

export const EventCluster: FC<ClusterProps> = ({
  calculatedHeading,
  center,
  events,
  eventsLength,
  hoveredEventId,
  isOngoing,
  onClick,
  setHoveredEventId,
  setSelectedEvent,
  showLocationUpdates,
}: ClusterProps): JSX.Element => {
  const { notableEvents } = useNotableEventsContext();
  const [hover, setHover] = useState(false);
  const [isClusterHover, setIsClusterHover] = useState(false);
  const [openedDialogCluster, setOpenedDialogCluster] = useState(false);
  const markerRef = useRef<L.Marker>(null);
  const map = useMap();
  const boundingMarker = createBoundingMarker(BOUNDING_RADIUS);
  const theme = useTheme();
  const {
    containsFirstEvent,
    containsLastEvent,
    notableEventData = [],
  } = useMemo(() => getNotableEventsInformation(events, eventsLength, notableEvents), [events, eventsLength, theme]);

  useEffect(() => {
    const closeDialog = (): void => {
      setOpenedDialogCluster(false);
    };

    window.addEventListener('resize', closeDialog);
    return () => window.removeEventListener('resize', closeDialog);
  }, []);

  const updatedNotableEventData = useMemo(() => notableEventData.sort((item1, item2) => item1.priority - item2.priority).sort((a, b) => {
    return a.eventData.occurred.getTime() - b.eventData.occurred.getTime();
  }), [notableEventData]);

  const { clickedEventId, setClickedEventId } = useHistorySearch();

  const mapEventData = updatedNotableEventData?.slice().filter((item) => {
    if (item.index === 0 || item.index === eventsLength - 1) return true;
    if (showLocationUpdates) return true;
    return (item.eventData.eventTypeId !== TrackingEventTypes.LOCATION_UPDATE);
  });

  const locationUpdatesCheck = notableEventData.every(({ eventData }) => hoveredEventId !== eventData.eventId && clickedEventId !== eventData.eventId && eventData.eventTypeId === TrackingEventTypes.LOCATION_UPDATE);
  const firstClusterEvent = mapEventData[0];
  const firstClusterEventData = firstClusterEvent?.eventData;
  const iconColor = events.find(({ eventData }) => firstClusterEventData?.eventId === eventData.eventId)?.iconColor ?? events[0].iconColor;
  const color = useMemo(() => firstClusterEventData && getNotableEventColor(firstClusterEventData, theme, notableEvents), [events]);
  const opacityCheck = !locationUpdatesCheck || containsFirstEvent || containsLastEvent || hover;
  const clusterColor = iconColor;
  const clusterContrast = (chroma(iconColor).luminance() < 0.5) ? theme.palette.common.white : theme.palette.common.black;
  const isSmallScreen = useSmallScreen();
  const clusterEventIds = updatedNotableEventData.map((event) => event.eventData.eventId);

  const isDialogCluster = mapEventData.length > 1;
  const isSingleEvent = mapEventData.length === 1;
  const hasEvents = mapEventData.length > 0;
  const clusterBubbleSize = getClusterBubbleMarkerSize(mapEventData.length);
  const isLocationCluster = (mapEventData.every((item) => item.eventData.eventTypeId === TrackingEventTypes.LOCATION_UPDATE) && !containsFirstEvent && !containsLastEvent);

  const eventHandlers = {
    mouseover: () => {
      if (mapEventData[0] && mapEventData[0].eventData.eventId) {
        setIsClusterHover(isDialogCluster);
        setHoveredEventId?.(mapEventData[0].eventData.eventId);
        setHover(true);
      }
    },
    mouseout: () => {
      setHoveredEventId?.(null);
      setIsClusterHover(false);
      setHover(false);
    },
    click: (e: L.LeafletMouseEvent) => {
      if (locationUpdatesCheck) return;
      e.originalEvent.stopPropagation();

      if (isDialogCluster) {
        setOpenedDialogCluster(true);
      }

      if (isSingleEvent) {
        setSelectedEvent?.(firstClusterEventData);
        setClickedEventId(firstClusterEventData?.eventId);
        if (firstClusterEventData) {
          onClick?.(0, [firstClusterEventData]);
        }
      }
    },
  };

  const onEventSelect: GridEventListener<'rowClick'> = ({ row }: { row: { id: string } }): void => {
    const clusterEvents = mapEventData.map((item) => item.eventData);
    const index = getEventDialogIndex(row.id, clusterEvents);
    setOpenedDialogCluster(false);
    onClick?.(index, clusterEvents);
  };

  const marker = L.marker(center, { icon: boundingMarker });
  const markerLatLng = marker.getLatLng();
  const isFirstMarkerInView = map.getBounds().contains(markerLatLng);

  useEffect(() => {
    const reset = (): void => {
      setClickedEventId(null);
      markerRef.current?.closeTooltip();
    };

    map.on('zoomend click moveend', reset);
    return () => {
      map.off('zoomend click moveend', reset);
    };
  }, []);

  useEffect(() => {
    if ((hoveredEventId && clusterEventIds.includes(hoveredEventId)) && hasEvents) {
      markerRef.current?.openTooltip();
      return;
    }
    markerRef.current?.closeTooltip();
  }, [hoveredEventId, clusterEventIds, markerRef.current, hasEvents]);

  return <>
    {!isFirstMarkerInView
      ? null
      : <Marker
        eventHandlers={eventHandlers}
        icon={boundingMarker}
        pane={(containsFirstEvent || containsLastEvent) ? MapPanes.START_END_MARKERS : undefined}
        position={center}
        zIndexOffset={1000}
      />
    }
    {(isSingleEvent) ? mapEventData.map((event, index) =>
      <EventClusterPoint
        key={index}
        calculatedHeading={calculatedHeading}
        center={center}
        event={event.eventData}
        firstPoint={event.index === 0}
        hoveredEventId={hoveredEventId}
        iconColor={iconColor}
        isOngoing={isOngoing}
        lastPoint={event.index === (eventsLength - 1)}
        markerSize={getMarkerSize(event.eventData.eventId, hoveredEventId) * (event.eventData.eventTypeId === TrackingEventTypes.LOCATION_UPDATE && event.index !== 0 && event.index !== eventsLength - 1 ? 0.75 : 1)}
        onClick={() => { }}
        opacity={opacityCheck}
        setHoveredEventId={setHoveredEventId}
        setSelectedEvent={setSelectedEvent}
      />,
    ) : (isDialogCluster) ? <Marker icon={createBubbleClusterMarker(clusterBubbleSize, clusterColor, clusterContrast, <Typography component='div' className='cluster-event-count' variant='body1'>{mapEventData.length}</Typography>)} position={center} eventHandlers={eventHandlers} /> : <></>
    }
    <Marker
      ref={markerRef}
      eventHandlers={eventHandlers}
      icon={
        createClusterMarker(
          color,
          getClusterMarkerSize(mapEventData.map((item) => item.eventData.eventId), hoveredEventId) * (firstClusterEvent?.eventData.eventTypeId === TrackingEventTypes.LOCATION_UPDATE && firstClusterEvent.index !== 0 && firstClusterEvent.index !== eventsLength - 1 ? 0.75 : 1),
          (containsFirstEvent) || firstClusterEvent?.index === 0,
          (containsLastEvent || firstClusterEvent?.index === (eventsLength - 1)),
          Boolean(isOngoing && (containsLastEvent || firstClusterEvent?.index === (eventsLength - 1))),
          iconColor,
          firstClusterEvent?.eventData.eventTypeId === TrackingEventTypes.LOCATION_UPDATE,
          theme,
          firstClusterEventData?.data.velocity?.heading ?? calculatedHeading,
        )
      }
      opacity={!hasEvents || isDialogCluster || (isLocationCluster && !showLocationUpdates) ? 0 : 1}
      pane={(containsFirstEvent || containsLastEvent) ? MapPanes.START_END_MARKERS : undefined}
      position={center}
      title={firstClusterEventData?.eventId}
      zIndexOffset={locationUpdatesCheck ? -1 : 290}
    >
      {!isSmallScreen && <ClusterTooltip
        direction="auto"
        expanded={false}
        offset={[getClusterTooltipOffset(clusterBubbleSize), 0]}
        type={isClusterHover ? 'summary' : 'event'}
        summaryData={mapEventData.map((item) => item.eventData)}
        eventData={notableEventData.map((item) => item.eventData)}
        eventId={hoveredEventId}
        position={center}
      />}
    </Marker>
    {!isSmallScreen ? <Dialog disableEnforceFocus closeAfterTransition={false} maxWidth='lg' open={openedDialogCluster} onClose={() => setOpenedDialogCluster(false)}>
      <Box width={theme.breakpoints.values.md}>
        <ClusterTabularData data={mapEventData.map((item) => item.eventData)} onEventSelect={onEventSelect} />
      </Box>
    </Dialog> : <StyledSwipeableDrawer anchor='bottom' disableSwipeToOpen open={openedDialogCluster} onOpen={() => { }} onClose={() => setOpenedDialogCluster(false)}>
      <Box position='absolute' top='0' right='0' m={1} zIndex={500}>
        <IconButton onClick={() => setOpenedDialogCluster(false)}><Icon icon="ChevronDown" /></IconButton>
      </Box>
      <Box height='98vh' width='100%'>
        <ClusterTabularData data={mapEventData.map((item) => item.eventData)} onEventSelect={onEventSelect} />
      </Box>
    </StyledSwipeableDrawer>}
  </>;
};

export default React.memo(EventCluster);
